import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth, UserRoleEnum } from './AuthContext';
import { db } from '../firebase';
import AppHeader from './AppHeader';
import { UserType } from '../types/plan';
import { PlanService } from '../services/PlanService';
import { PlanAccessValidator } from '../services/PlanAccessValidator';
import InfoModal from './InfoModal';
import PlansHub from './Plans/PlansHub';
import TherapistPlansLayout from './Plans/TherapistPlansLayout';
import ParticipantPlansLayout from './Plans/ParticipantPlansLayout';
import { PlanDisplay } from './Plans/PlanCard';
import { ParticipantIntent } from './Plans/PlansIntentBanner';
import { captureInfluencerRefFromSearch } from '../utils/influencerAttribution';
import { isMarketingHost, shouldEnforceDomainSplit } from '../constants/siteDomains';
import { goToApp } from '../utils/crossDomainNav';

enum PlanSection {
  TERAPEUTA = 'terapeuta',
  PARTICIPANTE = 'participante'
}

type Plan = PlanDisplay;

/** Filtra planos de participante por intenção (fallback: lista completa se vazio). */
function filterPlansByIntent(plans: Plan[], intent: ParticipantIntent | null): Plan[] {
  if (!intent || plans.length === 0) return plans;
  const blob = (p: Plan) => `${p.name} ${p.sessionLabel ?? ''} ${p.description ?? ''}`.toLowerCase();
  if (intent === 'participar') {
    const filtered = plans.filter((p) => /particip|represent|clube/.test(blob(p)));
    return filtered.length > 0 ? filtered : plans;
  }
  const filtered = plans.filter(
    (p) => /constel|sessão|sessao|sua/.test(blob(p)) && !/represent|particip/.test(blob(p))
  );
  if (filtered.length > 0) return filtered;
  const withoutParticipation = plans.filter((p) => !/represent|particip|clube/.test(blob(p)));
  return withoutParticipation.length > 0 ? withoutParticipation : plans;
}

export default function Plans() {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [processingPlan, setProcessingPlan] = React.useState<string | null>(null);
  const [dynamicTherapistPlans, setDynamicTherapistPlans] = useState<Plan[]>([]);
  const [dynamicParticipantPlans, setDynamicParticipantPlans] = useState<Plan[]>([]);
  const [isLoadingPlans, setIsLoadingPlans] = useState(true);
  
  // Estado do modal
  const [modalState, setModalState] = useState({
    isOpen: false,
    title: '',
    message: '',
    variant: 'warning' as 'error' | 'warning' | 'info' | 'success',
    redirectTo: ''
  });

  // Extrair parâmetro da URL para determinar seção específica
  const searchParams = new URLSearchParams(location.search);
  const querySectionRaw = searchParams.get('secao');
  const queryIntentRaw = searchParams.get('intencao');
  const participantIntent: ParticipantIntent | null =
    queryIntentRaw === 'participar' || queryIntentRaw === 'constelacao'
      ? queryIntentRaw
      : null;
  /** Hub com 3 caminhos — só quando não há ?secao na URL */
  const showHub = !querySectionRaw;
  /** Página dedicada de planos para consteladores (?secao=terapeuta) */
  const isTherapistPlansPage = querySectionRaw === PlanSection.TERAPEUTA;
  /** Página dedicada de planos para participantes (?secao=participante) */
  const isParticipantPlansPage = querySectionRaw === PlanSection.PARTICIPANTE;

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  React.useEffect(() => {
    captureInfluencerRefFromSearch(location.search);
  }, [location.search]);

  // Buscar planos dinâmicos do Firestore
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setIsLoadingPlans(true);
        const allPlans = await PlanService.fetchActivePlans(db);
        // Planos de terapeutas
        const therapistPlans: Plan[] = allPlans.filter(plan => plan.userType === UserType.THERAPIST).map((basePlan) => ({
          ...basePlan,
          type: 'terapeuta' as const,
          title: basePlan.name,
          price: PlanService.formatCurrency(basePlan.value),
          period: PlanService.formatPeriod(basePlan.quantity, basePlan.billingType, basePlan.isRecurring),
          requiresScheduling: false,
          actualPrice: basePlan.value
        }));
        therapistPlans.sort((a, b) => (a.value || 0) - (b.value || 0));
        setDynamicTherapistPlans(therapistPlans);
        // Planos de participantes
        const participantPlans: Plan[] = allPlans.filter(plan => plan.userType === UserType.PATIENT).map((basePlan) => ({
          ...basePlan,
          type: 'participante' as const,
          title: basePlan.name,
          price: PlanService.formatCurrency(basePlan.value),
          period: PlanService.formatPeriod(basePlan.quantity, basePlan.billingType, basePlan.isRecurring),
          requiresScheduling: false,
          actualPrice: basePlan.value
        }));
        participantPlans.sort((a, b) => (a.value || 0) - (b.value || 0));
        setDynamicParticipantPlans(participantPlans);
      } catch (error) {
        console.error('Erro ao buscar planos:', error);
      } finally {
        setIsLoadingPlans(false);
      }
    };
    fetchPlans();
  }, []);

  // Função para encontrar um plano pelo ID
  const findPlanById = (planId: string): Plan | null => {
    const allPlans = [...dynamicTherapistPlans, ...dynamicParticipantPlans];
    return allPlans.find(plan => plan.id === planId) || null;
  };

  const buildCheckoutPath = (planId: string, quantity?: number) => {
    const params = new URLSearchParams({ planId });
    if (quantity && Number.isInteger(quantity) && quantity > 0) {
      params.set('quantity', String(quantity));
    }
    return `/checkout?${params.toString()}`;
  };

  const handlePlanSelection = async (planId: string, quantity?: number) => {
    const checkoutPath = buildCheckoutPath(planId, quantity);

    if (shouldEnforceDomainSplit() && isMarketingHost()) {
      goToApp(checkoutPath);
      return;
    }

    if (!isAuthenticated) {
      navigate(checkoutPath, {
        state: { from: { pathname: location.pathname, search: location.search } },
      });
      return;
    }

    const selectedPlan = findPlanById(planId);
    if (!selectedPlan) {
      console.error('Plano não encontrado:', planId);
      return;
    }

    // Validar se o usuário cadastrado pode acessar este plano (convidado anônimo não entra aqui).
    if (isAuthenticated && user && user.role && selectedPlan.userType) {
      const validation = PlanAccessValidator.canAccessPlan(user.role, selectedPlan.userType);
      
      if (!validation.canAccess) {
        setModalState({
          isOpen: true,
          title: validation.title || 'Acesso Negado',
          message: validation.message || '',
          variant: validation.variant || 'warning',
          redirectTo: validation.redirectTo || ''
        });
        return;
      }
    }

    setProcessingPlan(planId);

    try {
      if (selectedPlan.redirectTo) {
        navigate(selectedPlan.redirectTo);
        return;
      }

      // Se o plano requer agendamento, redireciona para a página de agendamento
      if (selectedPlan.requiresScheduling) {
        goToApp(`/agendamento?plan=${planId}`, navigate);
        return;
      }

      navigate(checkoutPath, {
        state: { from: { pathname: location.pathname, search: location.search } },
      });

    } catch (error) {
      console.error('Erro ao processar seleção do plano:', error);
      setModalState({
        isOpen: true,
        title: 'Erro',
        message: 'Erro ao processar sua solicitação. Por favor, tente novamente.',
        variant: 'error',
        redirectTo: ''
      });
    } finally {
      setProcessingPlan(null);
    }
  };
  
  const displayedParticipantPlans = React.useMemo(
    () => filterPlansByIntent(dynamicParticipantPlans, participantIntent),
    [dynamicParticipantPlans, participantIntent]
  );

  return (
    <div className="min-h-screen bg-[#F3F6F1]">
      <AppHeader />

      <div className="pt-32 pb-16 md:pt-40 md:pb-24">
        <div className="container mx-auto px-4">
          {showHub ? (
            <PlansHub />
          ) : isTherapistPlansPage ? (
            <TherapistPlansLayout
              plans={dynamicTherapistPlans}
              isLoading={isLoadingPlans}
              processingPlan={processingPlan}
              onSelect={handlePlanSelection}
            />
          ) : isParticipantPlansPage ? (
            <ParticipantPlansLayout
              plans={displayedParticipantPlans}
              isLoading={isLoadingPlans}
              processingPlan={processingPlan}
              intent={participantIntent}
              onSelect={handlePlanSelection}
            />
          ) : null}
        </div>
      </div>
      
      <InfoModal
        isOpen={modalState.isOpen}
        onClose={() => {
          setModalState({ ...modalState, isOpen: false });
          if (modalState.redirectTo) {
            navigate(modalState.redirectTo);
          }
        }}
        title={modalState.title}
        message={modalState.message}
        variant={modalState.variant}
        buttonText="Entendi"
      />
    </div>
  );
}